// @ts-nocheck
import { Component } from '@theme/component';

/**
 * Select Component Web Component
 *
 * Event-driven select that manages visual state (success, error, default) based on external validation events.
 * Mirrors text-field.js pattern for consistency.
 *
 * The component:
 * - Listens for 'field:validate' events to update validation state
 * - Listens for 'field:setOptions' events to update dropdown options
 * - Listens for 'field:setValue' events to set value and sync UI
 * - Listens for 'field:setEnabled' events to enable/disable the whole control
 * - Listens for 'field:setAvailability' events to enable/disable individual options
 * - Toggles visibility of elements with showOn*** refs
 * - Updates error/success messages
 * - Adds/removes _error and _success classes for styling
 *
 * Required refs:
 * - `input`: hidden input with selected value
 * - `label`: label element inside trigger with current selected text
 * - `dropdown`: nested `dropdown-component` instance
 *
 * @extends {Component}
 */
class SelectField extends Component {
  /**
   * @typedef {Object} SelectComponentRefs
   * @property {HTMLInputElement} input Required hidden input (`ref="input"`).
   * @property {HTMLElement} label Required trigger label node (`ref="label"`).
   * @property {HTMLElement} dropdown Required dropdown component (`ref="dropdown"`).
   */

  /**
   * Required refs for the component to operate.
   *
   * @type {string[]}
   */
  requiredRefs = ['input', 'dropdown'];

  /** @type {string} */
  #searchBuffer = '';

  /** @type {ReturnType<typeof setTimeout> | null} */
  #searchTimer = null;

  /** @type {string} */
  #placeholderText = '';

  /** @type {MutationObserver | null} */
  #openObserver = null;

  /**
   * Current validation state: 'default' | 'success' | 'error'
   * @type {'default' | 'success' | 'error'}
   */
  state = 'default';

  /**
   * Set the field state and update UI
   * @param {'default' | 'success' | 'error'} newState
   * @param {string} [message]
   */
  setState(newState, message) {
    this.state = newState;

    // Hide all state-specific elements
    const states = ['default', 'success', 'error'];
    states.forEach((s) => {
      const refKey = `showOn${s.charAt(0).toUpperCase() + s.slice(1)}`;
      const elements = Array.isArray(this.refs[refKey]) ? this.refs[refKey] : [this.refs[refKey]];
      elements.forEach((el) => {
        if (el) {
          const htmlEl = /** @type {HTMLElement} */ (el);
          htmlEl.hidden = true;
          htmlEl.classList.add('_hidden');
        }
      });
    });

    // Show current state's elements
    const currentRefKey = `showOn${newState.charAt(0).toUpperCase() + newState.slice(1)}`;
    const currentElements = Array.isArray(this.refs[currentRefKey]) ? this.refs[currentRefKey] : [this.refs[currentRefKey]];
    currentElements.forEach((el) => {
      if (el) {
        const htmlEl = /** @type {HTMLElement} */ (el);
        htmlEl.hidden = false;
        htmlEl.classList.remove('_hidden');
      }
    });

    // Update error message if provided
    if (message !== undefined) {
      const errorElements = this.querySelectorAll('[data-field-error]');
      errorElements.forEach((el) => {
        if (el) {
          const htmlEl = /** @type {HTMLElement} */ (el);
          htmlEl.textContent = message;
        }
      });
    }

    // Add/remove error and success classes on dropdown ref for styling
    if (this.refs.dropdown) {
      const dropdownEl = Array.isArray(this.refs.dropdown) ? this.refs.dropdown[0] : this.refs.dropdown;
      if (dropdownEl) {
        const htmlEl = /** @type {HTMLElement} */ (dropdownEl);
        htmlEl.classList.remove('_error', '_success');
        if (newState === 'error') {
          htmlEl.classList.add('_error');
        } else if (newState === 'success') {
          htmlEl.classList.add('_success');
        }
      }
    }
  }

  /**
   * Handle field:validate event for validation state changes
   * @type {(e: Event) => void}
   */
  handleValidate = (/** @type {Event} */ e) => {
    e.stopPropagation();
    const customEvent = /** @type {CustomEvent} */ (e);
    const { state, message } = customEvent.detail || {};
    if (state) {
      this.setState(state, message);
    }
  };

  /**
   * Handle field:setOptions event to update dropdown options
   * @type {(e: Event) => void}
   */
  handleSetOptions = (/** @type {Event} */ e) => {
    e.stopPropagation();
    const customEvent = /** @type {CustomEvent} */ (e);
    const { options } = customEvent.detail || {};
    if (Array.isArray(options)) {
      this.setOptions(options);
    }
  };

  /**
   * Handle field:setValue event to set value and sync UI
   * @type {(e: Event) => void}
   */
  handleSetValue = (/** @type {Event} */ e) => {
    e.stopPropagation();
    const customEvent = /** @type {CustomEvent} */ (e);
    const { value, silent } = customEvent.detail || {};

    if (value !== undefined) {
      const input = this.#inputElement;
      if (!input) return;
      input.value = String(value);
      this.#syncFromValue(String(value));
      // `silent` lets external controllers (e.g. variant-picker) set the value
      // programmatically without re-triggering the change-driven update cycle.
      if (!silent) input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  /**
   * Handle field:setEnabled event to enable/disable the whole control.
   * @type {(e: Event) => void}
   */
  handleSetEnabled = (/** @type {Event} */ e) => {
    e.stopPropagation();
    const customEvent = /** @type {CustomEvent} */ (e);
    const { enabled } = customEvent.detail || {};
    this.setEnabled(enabled !== false);
  };

  /**
   * Handle field:setAvailability event to toggle per-option disabled state.
   * @type {(e: Event) => void}
   */
  handleSetAvailability = (/** @type {Event} */ e) => {
    e.stopPropagation();
    const customEvent = /** @type {CustomEvent} */ (e);
    const { available } = customEvent.detail || {};
    this.setAvailability(Array.isArray(available) ? available : null);
  };

  /**
   * Enable or disable the entire control by toggling its trigger.
   * @param {boolean} enabled
   * @returns {void}
   */
  setEnabled(enabled) {
    const dropdown = this.#dropdownElement;
    const trigger = /** @type {HTMLButtonElement | null} */ (dropdown?.querySelector('.dropdown__trigger') ?? null);
    if (trigger) trigger.disabled = !enabled;
  }

  /**
   * Set per-option availability. Pass `null` to enable all options.
   * @param {string[] | null} available List of selectable option values.
   * @returns {void}
   */
  setAvailability(available) {
    const allowed = available === null ? null : new Set(available.map((v) => String(v)));
    for (const optionButton of this.querySelectorAll('.dropdown__button[data-value]')) {
      if (optionButton.closest('[data-option-template]')) continue;
      const optionValue = /** @type {HTMLElement} */ (optionButton).dataset.value || '';
      const isAvailable = !allowed || allowed.has(optionValue);
      /** @type {HTMLButtonElement} */ (optionButton).disabled = !isAvailable;
      optionButton.classList.toggle('dropdown__button--disabled', !isAvailable);
    }
    this.#sortAvailableFirst();
  }

  #sortAvailableFirst() {
    const menu = this.querySelector('[data-dropdown-menu]');
    if (!menu) return;

    for (const list of [menu, ...menu.querySelectorAll('.dropdown__group-options')]) {
      const items = [...list.querySelectorAll(':scope > li.dropdown__item:not([data-option-template]):not([data-empty-state])')];
      if (items.length < 2) continue;

      const ranked = items.map((item, position) => {
        const index = Number(item.dataset.optionIndex);
        return {
          item,
          disabled: item.querySelector('.dropdown__button')?.disabled ? 1 : 0,
          index: Number.isNaN(index) ? position : index,
        };
      });
      ranked.sort((a, b) => a.disabled - b.disabled || a.index - b.index);
      for (const { item } of ranked) list.appendChild(item);
    }
  }

  /**
   * Syncs UI state from initial hidden input value.
   *
   * @returns {void}
   */
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('field:validate', this.handleValidate);
    this.addEventListener('field:reset', this.handleReset);
    this.addEventListener('field:setOptions', this.handleSetOptions);
    this.addEventListener('field:setValue', this.handleSetValue);
    this.addEventListener('field:setEnabled', this.handleSetEnabled);
    this.addEventListener('field:setAvailability', this.handleSetAvailability);
    this.addEventListener('keydown', this.#handleKeydown);
    this.#placeholderText = this.#labelElement?.dataset?.placeholder
      ?? this.#labelElement?.textContent?.trim()
      ?? '';
    this.setupUnitToggle();
    this.#syncFromValue(this.#inputElement?.value || '');
    this.#setupOpenObserver();
  }

  /**
   * Clean up event listeners on disconnect
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('field:validate', this.handleValidate);
    this.removeEventListener('field:reset', this.handleReset);
    this.removeEventListener('field:setOptions', this.handleSetOptions);
    this.removeEventListener('field:setValue', this.handleSetValue);
    this.removeEventListener('field:setEnabled', this.handleSetEnabled);
    this.removeEventListener('field:setAvailability', this.handleSetAvailability);
    this.removeEventListener('keydown', this.#handleKeydown);
    this.teardownUnitToggle();
    this.#openObserver?.disconnect();
    this.#openObserver = null;
    if (this.#searchTimer !== null) clearTimeout(this.#searchTimer);
  }

  setupUnitToggle() {
    const fieldContainer = this.closest('.field');
    const first = (Array.isArray(this.refs.unitFirstButton) ? this.refs.unitFirstButton[0] : this.refs.unitFirstButton)
      || (fieldContainer ? /** @type {HTMLElement | null} */ (fieldContainer.querySelector('[ref="unitFirstButton"]')) : null);
    const second = (Array.isArray(this.refs.unitSecondButton) ? this.refs.unitSecondButton[0] : this.refs.unitSecondButton)
      || (fieldContainer ? /** @type {HTMLElement | null} */ (fieldContainer.querySelector('[ref="unitSecondButton"]')) : null);
    this._unitFirstButton = first instanceof HTMLElement ? first : null;
    this._unitSecondButton = second instanceof HTMLElement ? second : null;
    if (!this._unitFirstButton && !this._unitSecondButton) return;

    this.unitToggleHandler = (/** @type {MouseEvent} */ event) => {
      event.preventDefault();
      const target = /** @type {HTMLElement} */ (event.currentTarget);
      const isFirst = target === this._unitFirstButton;
      const fromUnit = isFirst ? 2 : 1;
      const toUnit = isFirst ? 1 : 2;

      if (this._unitFirstButton) {
        this._unitFirstButton.classList.toggle('_active', isFirst);
        this._unitFirstButton.setAttribute('aria-pressed', String(isFirst));
      }
      if (this._unitSecondButton) {
        this._unitSecondButton.classList.toggle('_active', !isFirst);
        this._unitSecondButton.setAttribute('aria-pressed', String(!isFirst));
      }

      const input = this.#inputElement;
      const fieldName = input?.name || '';

      this.dispatchEvent(new CustomEvent('field:unit-change', {
        detail: {
          fieldName,
          fromUnit,
          toUnit,
        },
        bubbles: true,
        composed: true
      }));
    };

    if (this._unitFirstButton) this._unitFirstButton.addEventListener('click', this.unitToggleHandler);
    if (this._unitSecondButton) this._unitSecondButton.addEventListener('click', this.unitToggleHandler);
  }

  teardownUnitToggle() {
    if (this._unitFirstButton && this.unitToggleHandler) this._unitFirstButton.removeEventListener('click', this.unitToggleHandler);
    if (this._unitSecondButton && this.unitToggleHandler) this._unitSecondButton.removeEventListener('click', this.unitToggleHandler);
  }

  #setupOpenObserver() {
    const dropdown = this.#dropdownElement;
    if (!dropdown) return;

    this.#openObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const isOpen = /** @type {Element} */ (mutation.target).classList.contains('is-open');
          if (isOpen) {
            requestAnimationFrame(() => {
              const active = this.querySelector('.dropdown__button._active');
              active?.scrollIntoView({ block: 'nearest', behavior: 'instant' });
            });
          }
        }
      }
    });

    this.#openObserver.observe(dropdown, { attributes: true, attributeFilter: ['class'] });
  }

  handleReset = () => {
    const input = this.#inputElement;
    if (input) {
      input.value = '';
      this.#syncFromValue('');
    }
    this.setState('default');
  };

  /**
   * Update dropdown options dynamically.
   *
   * Accepts two shapes:
   *  - Flat: `string[]` or `Array<{ value, label }>`.
   *  - Grouped: `Array<{ groupName: string, options: Array<{ value, label } | string> }>`.
   *    Each group renders as a `<li class="dropdown__group">` wrapping a header element
   *    and a nested `<ul>` of option `<li>`s — so the group is one styleable container.
   *
   * @param {Array<string | { value: any, label?: any } | { groupName: string, options: Array<any> }>} options
   */
  setOptions(options) {
    const menu = this.querySelector('[data-dropdown-menu]');
    if (!menu) return;

    const template = this.querySelector('[data-option-template]');
    if (!template) return;

    // Clear previously rendered top-level items. Scoped to direct children so the
    // option template inside `menu` is untouched.
    menu.querySelectorAll(':scope > li:not([data-option-template])').forEach((el) => el.remove());

    if (options.length === 0) {
      const emptyItem = document.createElement('li');
      emptyItem.className = 'dropdown__item dropdown__item--empty';
      emptyItem.setAttribute('role', 'none');
      emptyItem.setAttribute('data-empty-state', '');
      emptyItem.innerHTML = '<span class="dropdown__empty-message">No options available</span>';
      menu.appendChild(emptyItem);
      return;
    }

    const isGrouped =
      options[0] !== null &&
      typeof options[0] === 'object' &&
      Array.isArray(options[0].options);

    if (isGrouped) {
      for (const group of options) {
        const wrapper = document.createElement('li');
        wrapper.className = 'dropdown__group';
        wrapper.setAttribute('role', 'none');

        if (group.groupName) {
          const header = document.createElement('div');
          header.className = 'dropdown__group-header';
          header.textContent = String(group.groupName);
          wrapper.appendChild(header);
        }

        const list = document.createElement('ul');
        list.className = 'dropdown__group-options';
        list.setAttribute('role', 'presentation');
        for (const opt of group.options) {
          this.#appendOption(list, template, opt);
        }
        wrapper.appendChild(list);

        menu.appendChild(wrapper);
      }
    } else {
      for (const opt of options) {
        this.#appendOption(menu, template, opt);
      }
    }

    // Re-sync UI to highlight currently selected option
    const inputEl = this.#inputElement;
    if (inputEl) this.#syncFromValue(inputEl.value);
  }

  /**
   * Clone the option template and append a single rendered option `<li>` to `parent`.
   * @private
   * @param {Element} parent
   * @param {Element} template
   * @param {string | { value: any, label?: any }} option
   */
  #appendOption(parent, template, option) {
    const value = (option !== null && typeof option === 'object') ? String(option.value ?? '') : String(option);
    const label = (option !== null && typeof option === 'object') ? String(option.label ?? value) : String(option);

    const clone = /** @type {HTMLElement} */ (template.cloneNode(true));
    clone.removeAttribute('data-option-template');
    clone.removeAttribute('hidden');
    clone.removeAttribute('aria-hidden');
    clone.dataset.optionIndex = String(parent.querySelectorAll(':scope > li:not([data-option-template])').length);

    const button = clone.querySelector('.dropdown__button');
    if (button) {
      button.setAttribute('data-value', value);
      button.setAttribute('on:click', `select-field/select?value=${encodeURIComponent(value)}&label=${encodeURIComponent(label)}`);

      const textSpan = button.querySelector('span:first-child');
      if (textSpan) {
        textSpan.textContent = label;
      }
    }

    parent.appendChild(clone);
  }

  /**
   * Handles option selection and syncs hidden input + visible label.
   *
   * @param {{ value?: string | number, label?: string } | string | number} data
   * @param {Event} event
   * @returns {void}
   */
  select = (data, event) => {
    event.preventDefault();

    // Prefer the clicked button's data-value over URL-parsed `data.value`.
    // The framework's parseValue coerces numeric-looking query params via Number(),
    // which strips trailing zeros ("1.0" → 1 → "1"). Reading the original string
    // attribute preserves the exact option value as rendered.
    const targetButton = event.target instanceof Element
      ? event.target.closest('.dropdown__button')
      : null;
    const buttonValue = targetButton?.getAttribute('data-value');
    const buttonLabel = targetButton?.querySelector('span:first-child')?.textContent?.trim();

    const value = buttonValue ?? (typeof data === 'object'
      ? String(data.value ?? '')
      : String(data ?? ''));
    const label = buttonLabel ?? (typeof data === 'object'
      ? String(data.label ?? value)
      : value);
    const input = this.#inputElement;
    if (!input) return;

    input.value = value;
    this.#applySelectedState(value, label, targetButton instanceof HTMLElement ? targetButton : null);

    const dropdown = /** @type {(HTMLElement & { close?: () => void }) | null} */ (this.#dropdownElement || null);
    if (dropdown?.close) {
      dropdown.close();
    }

    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Keyboard navigation: arrows, type-ahead, Home/End.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const dropdown = /** @type {(HTMLElement & { show?: () => void, isOpen?: boolean }) | null} */ (this.#dropdownElement);
    const isOpen = dropdown?.classList.contains('is-open') ?? false;

    const options = /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('.dropdown__button[data-value]')]
        .filter(btn => !btn.closest('[data-option-template]'))
    );

    if (!options.length) return;

    const { key } = event;

    if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();

      if (!isOpen) dropdown?.show?.();

      const currentIndex = options.indexOf(/** @type {HTMLElement} */ (document.activeElement));
      let nextIndex;
      if (key === 'ArrowDown') {
        nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0;
      } else {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1;
      }
      options[nextIndex]?.focus();
      return;
    }

    if (key === 'Home') {
      event.preventDefault();
      if (!isOpen) dropdown?.show?.();
      options[0]?.focus();
      return;
    }

    if (key === 'End') {
      event.preventDefault();
      if (!isOpen) dropdown?.show?.();
      options[options.length - 1]?.focus();
      return;
    }

    // Type-ahead: single printable character (exclude Space — it must fire native button click)
    if (key.length === 1 && key !== ' ' && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();

      if (this.#searchTimer !== null) clearTimeout(this.#searchTimer);
      this.#searchBuffer += key.toLowerCase();

      const findMatch = (/** @type {string} */ query) =>
        options.find((btn) => {
          const text = btn.querySelector('span:first-child')?.textContent?.trim().toLowerCase() ?? '';
          return text.startsWith(query);
        }) ?? null;

      let match = findMatch(this.#searchBuffer);

      // If multi-char buffer has no match, reset to last typed char and try again
      if (!match && this.#searchBuffer.length > 1) {
        this.#searchBuffer = key.toLowerCase();
        match = findMatch(this.#searchBuffer);
      }

      if (match) {
        if (!isOpen) {
          // Closed: select directly like native select
          match.click();
        } else {
          match.focus();
        }
      }

      this.#searchTimer = setTimeout(() => { this.#searchBuffer = ''; }, 500);
    }
  };

  /**
   * @param {string} value
   * @returns {void}
   */
  #syncFromValue(value) {
    if (value === '') {
      this.classList.remove('_selected');
      const labelElement = this.#labelElement;
      if (labelElement) {
        labelElement.textContent = this.#placeholderText;
      }
      for (const button of this.querySelectorAll('.dropdown__button')) {
        button.classList.remove('_active');
      }

      const button = Array.from(this.querySelectorAll('.dropdown__button')).find((btn) => btn.dataset.value === '' && !btn.parentNode.hidden);
      if (!button) return;

      const label = button.querySelector('span:first-child')?.textContent?.trim() || value;
      const labelText = button?.querySelector('span:first-child')?.textContent?.trim() || value;
      this.#applySelectedState(value, labelText, button);
      return;
    }

    const button = this.querySelector(`.dropdown__button[data-value="${CSS.escape(value)}"]`);
    const label = button?.querySelector('span:first-child')?.textContent?.trim() || value;
    this.#applySelectedState(value, label, button instanceof HTMLElement ? button : null);
  }

  /**
   * @param {string} value
   * @param {string} label
   * @param {HTMLElement | null} selectedButton
   * @returns {void}
   */
  #applySelectedState(value, label, selectedButton) {
    const labelElement = this.#labelElement;
    if (labelElement) labelElement.textContent = label;

    this.classList.toggle('_selected', value !== '');

    for (const button of this.querySelectorAll('.dropdown__button')) {
      button.classList.remove('_active');
    }

    const target = selectedButton?.classList.contains('dropdown__button')
      ? selectedButton
      : [...this.querySelectorAll('.dropdown__button')].find(
          (b) => b.getAttribute('data-value') === value || b.textContent?.trim() === label
        );

    target?.classList.add('_active');
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  get #inputElement() {
    const refInput = this.refs.input;
    if (refInput instanceof HTMLInputElement) return refInput;

    const fallback = this.querySelector('input[type="hidden"], input');
    return fallback instanceof HTMLInputElement ? fallback : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  get #labelElement() {
    const refLabel = this.refs.label;
    if (refLabel instanceof HTMLElement) return refLabel;

    const fallback = this.querySelector('[ref="label"]');
    return fallback instanceof HTMLElement ? fallback : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  get #dropdownElement() {
    const refDropdown = this.refs.dropdown;
    if (refDropdown instanceof HTMLElement) return refDropdown;

    const fallback = this.querySelector('dropdown-component');
    return fallback instanceof HTMLElement ? fallback : null;
  }
}

if (!customElements.get('select-field')) {
  customElements.define('select-field', SelectField);
}
